<script lang="ts">
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import LayoutSelectButtons from "@retailer/svelte/ProductLines/Index/LayoutSelectButtons.svelte";
  import MobileFiltersButton from "@retailer/svelte/ProductLines/MobileFiltersButton.svelte";
  import PerPageSelect from "@retailer/svelte/ProductLines/Index/PerPageSelect.svelte";
  import PriceSortHelperText from "@retailer/svelte/ProductLines/Index/PriceSortHelperText.svelte";
  import SelectedFiltersChips from "@shared/svelte/Components/ProductLines/SelectedFiltersChips.svelte";
  import SortingSelect from "@retailer/svelte/ProductLines/Index/SortingSelect.svelte";

  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import {
    DEFAULT_LIMIT,
    ProductLineListingStore,
  } from "@retailer/svelte/ProductLines/ProductLineContainer/stores/ProductLineListingStore";

  import { shouldShowFilters } from "@shared/services/filters";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { goto, params, page as pageInfo } from "@roxi/routify";

  import OpenProductGroupBanner from "@retailer/svelte/ProductLines/Index/OpenProductGroupBanner.svelte";
  import { onDestroy } from "svelte";

  export let hasProductLines: boolean;
  export let page: number = 1;
  export let perPage: number = DEFAULT_LIMIT;
  export let total: number = 0;

  let isPriceSorted: boolean = false;
  let searchValue: string = $params.search || "";

  const allowChangePageSize = stagedRolloutCheck("search__change_per_page_limit");

  // distributor and supplier(manufacturer) plps are searchable
  let searchableResults: boolean = false;
  let searchPlaceholder: string;
  if ($params.distributors?.length) {
    searchPlaceholder = $DistributorsStore.find(c => c.id == $params.distributors[0])?.name;
    searchableResults = true;
  } else if ($params?.manufacturerName) {
    searchPlaceholder = $params.manufacturerName;
    searchableResults = true;
  }

  const createPageDescription = () => {
    const from = 1 + (page - 1) * perPage;
    const count = total < perPage ? total : page * perPage;
    return `Showing ${from}-${count} of ${total} products`;
  };

  const onKeyPress = e => {
    if (e.code === "Enter") {
      search();
    }
  };

  const refreshState = params => {
    const currentPagePath = $pageInfo.path;
    $goto(currentPagePath, { ...params });
  };

  const search = () => refreshState({ ...$params, search: searchValue, page: 1 });

  let selectedFilters;
  let displayMobileFiltersButton: boolean = true;

  const unsubscribe = ProductLineListingStore.subscribe(n => {
    selectedFilters = n.selectedFilters;
    displayMobileFiltersButton = shouldShowFilters(n.selectedFilters, n.productsFound);
  });

  onDestroy(() => {
    unsubscribe();
  });

  $: mobileMode = $LayoutStore.isMobile;
  $: showPriceSortingText = isPriceSorted && !selectedFilters.some(f => f.filter.param === "z");
</script>

<OpenProductGroupBanner />

<div
  class="fizz-flex-row-responsive fizz-align-content-center fizz-full-width fizz-container"
  class:fizz-space-between={searchableResults || showPriceSortingText}
  class:fizz-justify-end={!searchableResults && !showPriceSortingText}
  data-testid="product-index-header"
>
  {#if searchableResults}
    <div
      class="fizz-flex-row-responsive fizz-align-content-center fizz-full-width-search"
      data-testid="searchable-results-input"
    >
      <div class="fizz-relative fizz-inline-block">
        <div class="fizz-input-text fizz-input-with-button">
          <input
            type="text"
            id="input-results-search"
            name="input-results-search"
            placeholder={`Search ${searchPlaceholder}`}
            on:keypress={onKeyPress}
            bind:value={searchValue}
          />
        </div>
        <button class="fizz-icon-button-small" on:click={search}>
          <Icon type="Search" width="14" height="14" />
        </button>
      </div>
    </div>
  {/if}
  {#if mobileMode && displayMobileFiltersButton}
    <MobileFiltersButton />
  {/if}
  {#if mobileMode && allowChangePageSize}
    <div class="fizz-full-width">
      <PerPageSelect stateParams={$params} {refreshState} {mobileMode} {page} {total} />
    </div>
  {/if}
  <div class="fizz-full-width">
    {#if hasProductLines}
      <div class="fizz-flex-row-8 fizz-justify-right fizz-space-between">
        {#if !mobileMode}
          {#if allowChangePageSize}
            <PerPageSelect stateParams={$params} {refreshState} {mobileMode} {page} {total} />
          {/if}
          <div class="fizz-justify-start fizz-full-width">
            {createPageDescription()}
          </div>
        {/if}
        <LayoutSelectButtons />

        <SortingSelect stateParams={$params} {refreshState} bind:isPriceSorted />
      </div>
    {/if}
  </div>

  {#if mobileMode}
    <SelectedFiltersChips bind:selectedFilters />
  {/if}
</div>

{#if showPriceSortingText}
  <PriceSortHelperText />
{/if}
